/**
 * People-search endpoint — recipient/attendee autocomplete (June 2026).
 *
 * Powers the ``RecipientsRenderer`` in ``@ando/ui/ActionFieldEditor``.
 * Per ``feedback_action_forms_account_picker_first.md``: every HITL
 * action form starts with an account picker and recipient suggestions
 * are scoped to THAT chosen account — never the union of the user's
 * connections. The ``connection_id`` query param is therefore mandatory
 * at the call-site (the FE always has one once the user picks the
 * sender); the BE silently degrades to empty when the id doesn't match
 * a satisfying claim, so the renderer falls back to free-text chips.
 *
 * BE source of truth: ``src/api/v1/endpoints/people_search.py``
 * (wraps :class:`PeopleSearchService` — Google People `searchContacts` +
 * `searchDirectoryPeople`, Microsoft Graph `/me/people`).
 */

import { apiClient } from "../ApiClient.js";

export interface PeopleSearchHit {
  readonly email: string;
  readonly display_name: string;
  readonly provider: "google" | "microsoft" | "other";
  readonly job_title?: string;
}

export interface PeopleSearchResponse {
  readonly items: readonly PeopleSearchHit[];
}

export interface PeopleSearchParams {
  /** Free-text query — name fragment or partial email. Empty allowed. */
  readonly q: string;
  /**
   * Scopes the search to a single connected account (Gmail mailbox,
   * Outlook calendar, …). Null = no account picked yet → callers should
   * skip the network round-trip and stay in free-text mode.
   */
  readonly connectionId: string | null;
  /** Soft cap on returned hits. Defaults to 10. */
  readonly limit?: number;
  /** AbortController signal forwarded to ``apiClient``. */
  readonly signal?: AbortSignal;
}

/**
 * Search people across the connected account's directory + personal
 * contacts. Always resolves — an empty list is the normal "no hits"
 * state, not an error. Throws only on transport-level failures the
 * caller may want to surface (e.g. 401 — re-auth needed).
 */
export async function searchPeople({
  q,
  connectionId,
  limit = 10,
  signal,
}: PeopleSearchParams): Promise<readonly PeopleSearchHit[]> {
  if (!connectionId) return [];
  const params = new URLSearchParams({ q, limit: String(limit) });
  params.set("connection_id", connectionId);
  const resp = await apiClient.get<PeopleSearchResponse>(
    `/people/search?${params.toString()}`,
    { signal },
  );
  return resp.items;
}
